
import React, { useState, useEffect } from 'react';
import { parseMessageContent } from '@/utils/messageParser';
import { MessagePart } from '@/utils/messageParser/types';
import MessagePartRenderer from './MessagePartRenderer';
import { useCertificationStyles } from '@/hooks/useCertificationStyles';
import RetryMessage from './RetryMessage';

interface MessageContentProps {
  content: string;
  isUser: boolean;
  sessionId?: string;
  onSendMessage?: (message: string) => void;
  messageTimestamp?: Date | number;
  isRetryable?: boolean;
  onRetry?: () => Promise<void>;
  skipIntroAndClosing?: boolean; // Skip intro/closing when they were already streamed
}

const MessageContent: React.FC<MessageContentProps> = ({ content, isUser, sessionId, onSendMessage, messageTimestamp, isRetryable, onRetry, skipIntroAndClosing }) => {
  const { styles } = useCertificationStyles();
  const [isRetrying, setIsRetrying] = useState(false);
  
  const handleSendMessage = (message: string) => {
    if (onSendMessage) {
      onSendMessage(message);
    }
  };
  
  const handleRetry = async () => {
    if (!onRetry) return;
    
    setIsRetrying(true);
    try {
      await onRetry();
    } catch (error) {
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const [parts, setParts] = useState<MessagePart[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;
    
    const parseParts = async () => {
      setIsLoading(true);
      try {
        const parsedParts = await parseMessageContent(content, { sessionId, skipIntroAndClosing });
        
        // Only update state if this effect hasn't been cancelled
        if (!isCancelled) {
          setParts(parsedParts);
        }
      } catch (error) {
        console.error('Error parsing message content:', error);
        if (!isCancelled) {
          setParts([{ type: 'text', content: 'Error parsing message' }]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    parseParts();
    
    // Cleanup function to prevent race conditions
    return () => {
      isCancelled = true;
    };
  }, [content, skipIntroAndClosing]);

  // If loading or no parts to display, don't render anything
  if (isLoading || parts.length === 0) {
    return null;
  }
  
  // If this is a retryable error message, show the retry UI instead of normal content
  if (isRetryable && !isUser && content.startsWith('Error:')) {
    return <RetryMessage onRetry={handleRetry} isLoading={isRetrying} />;
  }

  // When skipIntroAndClosing is true, we're inside a unified bubble, so don't add wrapper
  if (skipIntroAndClosing) {
    return (
      <>
        {parts.map((part, index) => {
          const contentKey = typeof part.content === 'string' 
            ? part.content.slice(0, 50) 
            : JSON.stringify(part.content).slice(0, 50);
          const stableKey = `${part.type}-${index}-${contentKey.replace(/[^a-zA-Z0-9]/g, '')}`;
          
          return (
            <div key={stableKey} className={`w-full ${index > 0 && parts[index-1].type === 'text' && part.type === 'text' ? 'mt-2' : index > 0 ? 'mt-4' : ''}`}>
              <MessagePartRenderer
                part={part}
                isUser={isUser}
                onSendMessage={handleSendMessage}
                messageTimestamp={messageTimestamp}
              />
            </div>
          );
        })}
      </>
    );
  }

  return (
    <div 
      className={`${isUser ? 'bg-gray-600' : 'bg-gray-100'} p-3 max-w-full break-words overflow-hidden`}
      style={{ borderRadius: `${styles.corner_radius}px` }}
    >
      {parts.map((part, index) => {
        // Create a stable key based on part type and a hash of content
        const contentKey = typeof part.content === 'string' 
          ? part.content.slice(0, 50) 
          : JSON.stringify(part.content).slice(0, 50);
        const stableKey = `${part.type}-${index}-${contentKey.replace(/[^a-zA-Z0-9]/g, '')}`;
        
        return (
        <div key={stableKey} className={`w-full ${index > 0 && parts[index-1].type === 'text' && part.type === 'text' ? 'mt-2' : index > 0 ? 'mt-4' : ''}`}>
          <MessagePartRenderer
            part={part}
            isUser={isUser}
            onSendMessage={handleSendMessage}
            messageTimestamp={messageTimestamp}
            
          />
        </div>
        );
      })}
    </div>
  );
};

export default MessageContent;
